/**
 * App: Root component with hash-based routing.
 * Wraps the app with authentication and the catalog provider, then renders
 * AppContent which holds all routing/page logic.
 */

import React, { Component, useMemo } from "react";
import AuthGuard from "./components/AuthGuard";
import { AuthProvider, useAuth } from "./lib/auth-context";
import { CatalogProvider } from "./lib/catalog-context";
import { ThemeProvider } from "./lib/theme-context";
import AppContent from "./AppContent";
import { reportError } from "./lib/newrelic";
import { IS_DEV } from "./lib/env";
import type { MarketplaceClientConfig } from "./types/index";

class ErrorBoundary extends Component<
  { children: React.ReactNode },
  { error: Error | null }
> {
  state: { error: Error | null } = { error: null };

  static getDerivedStateFromError(error: Error) {
    return { error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error("Uncaught render error:", error, errorInfo);
    reportError(error, "react-error-boundary", {
      componentStack: errorInfo.componentStack ?? "",
    });
  }

  render() {
    if (this.state.error) {
      return (
        <div style={{ padding: "2rem", textAlign: "center" }}>
          <h1>Something went wrong</h1>
          <p style={{ color: "var(--color-text-secondary)" }}>{this.state.error.message}</p>
          <button
            onClick={() => window.location.reload()}
            style={{
              marginTop: "1rem",
              padding: "0.5rem 1rem",
              fontSize: "1rem",
              backgroundColor: "var(--color-brand-emphasis)",
              color: "var(--color-on-brand)",
              border: "none",
              borderRadius: "4px",
              cursor: "pointer",
            }}
          >
            Reload
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

// Bridges the auth context's token getter into the catalog client config.
// Lives inside AuthProvider so it can read useAuth(); the catalog fetch stays
// gated behind AuthGuard above it.
function CatalogBridge({ children }: { children: React.ReactNode }) {
  const { getApiToken } = useAuth();
  const catalogConfig = useMemo<Partial<MarketplaceClientConfig>>(
    () => ({ getToken: IS_DEV ? undefined : getApiToken }),
    [getApiToken],
  );

  return <CatalogProvider config={catalogConfig}>{children}</CatalogProvider>;
}

export default function App() {
  return (
    <ErrorBoundary>
      <ThemeProvider>
        <AuthProvider>
          <AuthGuard>
            <CatalogBridge>
              <AppContent />
            </CatalogBridge>
          </AuthGuard>
        </AuthProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}
